import React, { useEffect, useState } from 'react';
import { CheckIcon, MailIcon, MessageCircleIcon, PhoneIcon } from 'lucide-react';
import { advisor } from '../data/catalog';
import { LEAD_WEBHOOK, postToWebhook, track } from '../config/site';
import { Reveal } from './Reveal';
import { useLanguage } from '../contexts/LanguageContext';
import type { Copy } from '../data/i18n';
interface LeadState {
  nombre: string;
  email: string;
  telefono: string;
  interes: string;
  plazo: string;
  mensaje: string;
}
const fieldClass = 'w-full border border-line bg-canvas px-4 py-3 text-[14px] text-white placeholder:text-mute transition-colors duration-500 ease-[var(--ease-lux)] focus:border-guards focus:outline-none';
const labelClass = 'block text-[10px] uppercase tracking-[0.24em] text-mute';

// ------------------------------------------------------------
// Arma el mensaje de WhatsApp con los datos del lead, en el
// idioma que el visitante está viendo. Es el plan B cuando no
// hay webhook configurado: el vendedor recibe el lead igual.
// ------------------------------------------------------------
function buildWhatsappMessage(lead: LeadState, t: Copy): string {
  const f = t.lead.whatsappFields;
  return [t.lead.whatsappIntro, '', `${f.name}: ${lead.nombre}`, `${f.phone}: ${lead.telefono}`, `${f.email}: ${lead.email}`, `${f.interest}: ${lead.interes}`, `${f.timeline}: ${lead.plazo}`, lead.mensaje ? `${f.message}: ${lead.mensaje}` : ''].filter(Boolean).join('\n');
}
export function LeadForm() {
  const {
    t,
    lang
  } = useLanguage();
  const emptyLead = (): LeadState => ({
    nombre: '',
    email: '',
    telefono: '',
    interes: t.lead.interests[0],
    plazo: t.lead.timelines[0],
    mensaje: ''
  });
  const [lead, setLead] = useState<LeadState>(emptyLead);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  // Al cambiar de idioma, los selectores muestran las opciones traducidas.
  useEffect(() => {
    setLead(prev => ({
      ...prev,
      interes: t.lead.interests[0],
      plazo: t.lead.timelines[0]
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);
  const update = (key: keyof LeadState, value: string) => {
    setLead(prev => ({
      ...prev,
      [key]: value
    }));
    setSent(false);
  };
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    track('Lead', {
      content_category: 'Quote',
      content_name: lead.interes,
      lang
    });
    const delivered = await postToWebhook(LEAD_WEBHOOK, {
      ...lead,
      lang
    });
    if (!delivered) {
      const url = `${advisor.whatsappHref}?text=${encodeURIComponent(buildWhatsappMessage(lead, t))}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    setSending(false);
    setSent(true);
    setLead(emptyLead());
  };
  const firstName = advisor.name.split(' ')[0];
  return <section id="contacto" aria-labelledby="contacto-title" className="scroll-mt-24 border-t border-line bg-canvas py-20 sm:py-28 lg:py-40" data-id="element-198" data-mp="1p8ut5klybvg4">
      <div className="mx-auto grid max-w-[1520px] grid-cols-1 gap-12 px-5 sm:gap-16 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:gap-24 lg:px-14" data-id="element-199" data-mp="9o2hrf2qmeku">
        {/* Asesor asignado */}
        <Reveal data-id="element-200" data-mp="ipa4r0zszszm">
          <div data-id="element-201" data-mp="ipsaco8cvjk7">
            <p className="text-[11px] uppercase tracking-[0.34em] text-mute" data-id="element-202" data-mp="ipifoncd8o86">
              {t.lead.advisorEyebrow}
            </p>
            <div className="mt-8 border border-line bg-surface" data-id="element-203" data-mp="3i68fg4v05wdw">
              <img src={advisor.photo} alt={`${advisor.name}, ${t.lead.advisorRole}`} loading="lazy" className="aspect-[3/2] w-full object-cover object-top sm:aspect-[4/5]" data-id="element-204" data-mp="3m57e57q54www" />
              <div className="border-t border-line p-7" data-id="element-205" data-mp="3vxn00g4k65n2">
                <h3 className="text-2xl font-light tracking-tight text-white" data-id="element-206" data-mp="2yfmwqt9my7pq">{advisor.name}</h3>
                <p className="mt-2 text-[12px] uppercase tracking-[0.18em] text-guards-bright" data-id="element-207" data-mp="33eg2fziokufc">
                  {t.lead.advisorRole}
                </p>
                <p className="mt-5 text-sm font-light leading-relaxed text-mute" data-id="element-208" data-mp="14xnzulebce1j">
                  {t.lead.advisorTagline}
                </p>

                <ul className="mt-7 space-y-3" data-id="element-209" data-mp="1ipvsc0ykdiuf">
                  <li data-id="element-210" data-mp="1dydqzbsmpg8x">
                    <a href={advisor.phoneHref} onClick={() => track('Contact', {
                    method: 'phone'
                  })} className="flex items-center gap-3 text-[15px] text-white transition-colors duration-500 ease-[var(--ease-lux)] hover:text-guards-bright" data-id="element-211" data-mp="1dxu6pofiafrq">
                      <PhoneIcon className="h-4 w-4 text-guards" aria-hidden="true" data-id="element-212" data-mp="21mn9o4ujpnvu" />
                      {advisor.phoneLabel}
                    </a>
                  </li>
                  <li data-id="element-213" data-mp="795go2bdfytf">
                    <a href={advisor.emailHref} onClick={() => track('Contact', {
                    method: 'email'
                  })} className="flex items-center gap-3 break-all text-[15px] text-white transition-colors duration-500 ease-[var(--ease-lux)] hover:text-guards-bright" data-id="element-214" data-mp="7aoy3w8rkfws">
                      <MailIcon className="h-4 w-4 text-guards" aria-hidden="true" data-id="element-215" data-mp="3td7dtunqeyja" />
                      {advisor.email}
                    </a>
                  </li>
                </ul>

                <a href={advisor.whatsappHref} target="_blank" rel="noreferrer" onClick={() => track('Contact', {
                method: 'whatsapp'
              })} className="btn-glint btn-glint-sheen btn-halo-blue mt-7 inline-flex w-full items-center justify-center gap-2 border border-gentian-bright/70 px-6 py-3 text-[13px] font-medium tracking-wide text-white transition-colors duration-500 ease-[var(--ease-lux)] hover:border-gentian-bright hover:bg-gentian/25" data-id="element-216" data-mp="3kgyeefzr7zx5">
                  <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-217" data-mp="zt3orthcusec" />
                  <span data-id="element-218" data-mp="xdx7fkuqxjuf">{t.lead.whatsapp}</span>
                </a>

                <p className="mt-5 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-mute" data-id="element-219" data-mp="xc2kk0zwzhnp">
                  <span className="h-1.5 w-1.5 rounded-full bg-gold" aria-hidden="true" data-id="element-220" data-mp="tey2w1cxfp3i" />
                  {t.lead.responseTime}
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Formulario de lead */}
        <Reveal delay={0.08} data-id="element-221" data-mp="1h0ut3nbgm3z4">
          <div data-id="element-222" data-mp="1h48vuwo1cf73">
            <p className="text-[11px] uppercase tracking-[0.34em] text-mute" data-id="element-223" data-mp="1c332cwmjwnwi">{t.lead.eyebrow}</p>
            <h2 id="contacto-title" className="mt-5 max-w-xl text-[34px] font-light tracking-[-0.03em] text-white lg:text-[52px]" data-id="element-224" data-mp="1c5fkpaw810a3">
              {t.lead.title}
            </h2>
            <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-mute" data-id="element-225" data-mp="1zsdxnyl4tq39">
              {t.lead.subtitle(firstName)}
            </p>

            <form onSubmit={onSubmit} className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2" data-id="element-226" data-mp="1v0cpoouzylj1">
              {/* Trampa anti-spam: invisible para personas, los bots la llenan. */}
              <div className="hidden" aria-hidden="true" data-id="element-227" data-mp="27d82djns1br8">
                <label htmlFor="lead-website" data-id="element-228" data-mp="2cb9lg4l7db2p">{t.lead.honeypot}</label>
                <input id="lead-website" name="website" type="text" tabIndex={-1} autoComplete="off" data-id="element-229" data-mp="2cdcih8krw4gx" />
              </div>

              <div data-id="element-230" data-mp="2qarc6pi26yep">
                <label htmlFor="lead-nombre" className={labelClass} data-id="element-231" data-mp="2qa97r5l8tdvp">
                  {t.lead.name}
                </label>
                <input id="lead-nombre" name="nombre" required autoComplete="name" value={lead.nombre} onChange={e => update('nombre', e.target.value)} placeholder={t.lead.namePlaceholder} className={`mt-3 ${fieldClass}`} data-id="element-232" data-mp="2v32e1xwd0u9w" />
              </div>

              <div data-id="element-233" data-mp="3uaw6bnn3pe67">
                <label htmlFor="lead-telefono" className={labelClass} data-id="element-234" data-mp="3uejxig52pohr">
                  {t.lead.phone}
                </label>
                <input id="lead-telefono" name="telefono" type="tel" required autoComplete="tel" value={lead.telefono} onChange={e => update('telefono', e.target.value)} placeholder="+1 (305) 000-0000" className={`mt-3 ${fieldClass}`} data-id="element-235" data-mp="850m4t1l031l" />
              </div>

              <div className="sm:col-span-2" data-id="element-236" data-mp="3lckh9q1vuofv">
                <label htmlFor="lead-email" className={labelClass} data-id="element-237" data-mp="3gl2gp33rupvf">
                  {t.lead.email}
                </label>
                <input id="lead-email" name="email" type="email" required autoComplete="email" value={lead.email} onChange={e => update('email', e.target.value)} placeholder={t.lead.emailPlaceholder} className={`mt-3 ${fieldClass}`} data-id="element-238" data-mp="2suygl5psl896" />
              </div>

              <div data-id="element-239" data-mp="uf93pw70go9p">
                <label htmlFor="lead-interes" className={labelClass} data-id="element-240" data-mp="ubl9eutqrxtx">
                  {t.lead.interest}
                </label>
                <select id="lead-interes" name="interes" value={lead.interes} onChange={e => update('interes', e.target.value)} className={`mt-3 ${fieldClass}`} data-id="element-241" data-mp="1m53ttv2p0hzf">
                  {t.lead.interests.map(item => <option key={item} value={item} className="bg-canvas" data-id="element-242" data-mp="1i27gais0fc2c">
                      {item}
                    </option>)}
                </select>
              </div>

              <div data-id="element-243" data-mp="6p4xdh2fuaf0">
                <label htmlFor="lead-plazo" className={labelClass} data-id="element-244" data-mp="6pofoyw030tk">
                  {t.lead.timeline}
                </label>
                <select id="lead-plazo" name="plazo" value={lead.plazo} onChange={e => update('plazo', e.target.value)} className={`mt-3 ${fieldClass}`} data-id="element-245" data-mp="1kwwmlofdymkv">
                  {t.lead.timelines.map(item => <option key={item} value={item} className="bg-canvas" data-id="element-246" data-mp="116t7xvmxagf6">
                      {item}
                    </option>)}
                </select>
              </div>

              <div className="sm:col-span-2" data-id="element-247" data-mp="s64scwhvs806">
                <label htmlFor="lead-mensaje" className={labelClass} data-id="element-248" data-mp="s76djtsm2avm">
                  {t.lead.message}
                </label>
                <textarea id="lead-mensaje" name="mensaje" rows={4} value={lead.mensaje} onChange={e => update('mensaje', e.target.value)} placeholder={t.lead.messagePlaceholder} className={`mt-3 resize-none ${fieldClass}`} data-id="element-249" data-mp="x72c3dijha3s" />
              </div>

              <div className="flex flex-col gap-5 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between" data-id="element-250" data-mp="2wlq6sfrlbbcg">
                <button type="submit" disabled={sending} className="btn-glint btn-glint-sheen btn-halo-red inline-flex w-full items-center justify-center bg-guards px-10 py-4 text-[13px] font-medium tracking-wide text-white transition-colors duration-500 ease-[var(--ease-lux)] hover:bg-guards-bright disabled:cursor-wait disabled:opacity-70 sm:w-fit" data-id="element-251" data-mp="3ah2erouraybb">
                  <span data-id="element-252" data-mp="3pbysuip8yfwb">{sending ? t.lead.sending : t.lead.submit}</span>
                </button>
                <p className="max-w-xs text-[11px] leading-relaxed text-mute" data-id="element-253" data-mp="1yzsy1a30ikdp">
                  {t.lead.consent(advisor.name, advisor.phoneLabel, advisor.email)}
                </p>
              </div>
            </form>

            {sent && <p role="status" className="mt-8 flex items-center gap-3 border border-guards/50 bg-guards/10 px-5 py-4 text-[14px] text-white" data-id="element-254" data-mp="3u4s82o4xmsuk">
                <CheckIcon className="h-4 w-4 text-guards-bright" aria-hidden="true" data-id="element-255" data-mp="7v7onjrdkvqr" />
                {t.lead.success(firstName)}
              </p>}
          </div>
        </Reveal>
      </div>
    </section>;
}